import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  Stack,
  Typography,
  Alert,
  Snackbar,
  CircularProgress,
  Box,
} from '@mui/material';
import DictationTextField from './DictationTextField';
import { reflectionsApi } from '../api/services';

interface GuidedReflectionDialogProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  date?: string;
  trigger?: string;
}

const STEPS = [
  'What went well today?',
  "What didn't go as planned?",
  'What adjustments will you make?',
  'Any insights or patterns?',
  'Review & Submit',
];

const STEP_PLACEHOLDERS = [
  'Reflect on your wins, accomplishments, and things that worked well...',
  'Think about challenges, blockers, or things that could have gone better...',
  'Consider changes to your approach, priorities, or schedule...',
  'Notice any recurring themes, lessons learned, or broader patterns...',
];

function formatToday(): string {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function compileContent(answers: string[]): string {
  const sections = [
    { heading: 'What went well', text: answers[0] },
    { heading: "What didn't go as planned", text: answers[1] },
    { heading: 'Adjustments', text: answers[2] },
    { heading: 'Insights', text: answers[3] },
  ];

  return sections
    .filter((s) => s.text.trim())
    .map((s) => `## ${s.heading}\n${s.text.trim()}`)
    .join('\n\n');
}

export default function GuidedReflectionDialog({
  open,
  onClose,
  onSaved,
  date,
  trigger = 'Manual',
}: GuidedReflectionDialogProps) {
  const [activeStep, setActiveStep] = useState(0);
  const [answers, setAnswers] = useState(['', '', '', '']);
  const [reviewContent, setReviewContent] = useState('');
  const [reviewEdited, setReviewEdited] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const isLastStep = activeStep === STEPS.length - 1;
  const isReviewStep = activeStep === STEPS.length - 1;

  const handleAnswerChange = useCallback(
    (index: number) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setAnswers((prev) => {
        const next = [...prev];
        next[index] = event.target.value;
        return next;
      });
    },
    [],
  );

  const handleAnswerTranscript = useCallback(
    (index: number) => (text: string) => {
      setAnswers((prev) => {
        const next = [...prev];
        next[index] = text;
        return next;
      });
    },
    [],
  );

  const handleNext = useCallback(() => {
    if (activeStep === STEPS.length - 2) {
      // Moving to review step: compile content
      setReviewContent(compileContent(answers));
      setReviewEdited(false);
    }
    setActiveStep((prev) => prev + 1);
  }, [activeStep, answers]);

  const handleBack = useCallback(() => {
    setActiveStep((prev) => prev - 1);
  }, []);

  const handleReviewChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setReviewContent(event.target.value);
      setReviewEdited(true);
    },
    [],
  );

  const handleReviewTranscript = useCallback((text: string) => {
    setReviewContent(text);
    setReviewEdited(true);
  }, []);

  const handleSubmit = useCallback(async () => {
    setSaving(true);
    setError(null);

    const finalContent = reviewEdited ? reviewContent : compileContent(answers);
    const insights = answers[3].trim() || undefined;
    const adjustmentsMade = answers[2].trim() || undefined;
    const reflectionDate = date || formatToday();

    try {
      await reflectionsApi.create({
        date: reflectionDate,
        trigger,
        content: finalContent,
        insights,
        adjustmentsMade,
      });
      setSnackbarOpen(true);
      onSaved();
      handleReset();
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          err.message ||
          'Failed to save reflection. Please try again.',
      );
    } finally {
      setSaving(false);
    }
  }, [reviewEdited, reviewContent, answers, date, trigger, onSaved]);

  const handleReset = useCallback(() => {
    setActiveStep(0);
    setAnswers(['', '', '', '']);
    setReviewContent('');
    setReviewEdited(false);
    setError(null);
  }, []);

  const handleClose = useCallback(() => {
    onClose();
    // Delay reset so the dialog animation completes before clearing state
    setTimeout(handleReset, 300);
  }, [onClose, handleReset]);

  const hasAnyContent = answers.some((a) => a.trim().length > 0);

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>Guided Reflection</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Stepper activeStep={activeStep} alternativeLabel>
              {STEPS.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {!isReviewStep ? (
              <Box>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {STEPS[activeStep]}
                </Typography>
                <DictationTextField
                  multiline
                  rows={4}
                  fullWidth
                  placeholder={STEP_PLACEHOLDERS[activeStep]}
                  value={answers[activeStep]}
                  onChange={handleAnswerChange(activeStep)}
                  onTranscriptChange={handleAnswerTranscript(activeStep)}
                  autoFocus
                />
              </Box>
            ) : (
              <Box>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Review your reflection
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Review the compiled reflection below. You can edit it before submitting.
                </Typography>
                <DictationTextField
                  multiline
                  rows={12}
                  fullWidth
                  value={reviewContent}
                  onChange={handleReviewChange}
                  onTranscriptChange={handleReviewTranscript}
                />
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <Box sx={{ flex: 1 }} />
          {activeStep > 0 && (
            <Button onClick={handleBack} disabled={saving}>
              Back
            </Button>
          )}
          {isLastStep ? (
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={saving || !hasAnyContent}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {saving ? 'Saving...' : 'Submit Reflection'}
            </Button>
          ) : (
            <Button variant="contained" onClick={handleNext}>
              Next
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        message="Reflection saved successfully"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
}
